import * as React from 'react';
import { connect } from 'react-redux';
import { getOperationSafe } from 'selectors';
import { IState } from 'services/root-reducer';
import {
    GenericOverlayOperation,
    actions as overlayWindowsActions
} from 'services/overlay-windows/overlay-windows-service';
import { actions as appActions } from 'services/app/app-service';
import { IOperation, EOperationStatus } from 'types';
import {
    OverlayContainer,
    AutoScrollbars,
    CloseButton,
    OverlayFormInput,
    Button,
    OverlayFormTextArea,
    FileChooser,
    Preloader
} from 'components';

import './style.scss';
import { Captcha } from 'components/captcha';
import { WithTranslation, withTranslation, Trans } from 'react-i18next';
import { IOperationsState } from 'services/operations/operations-reducer';
import { validateEmail } from 'utils/validation';
import { OverlayMessage } from 'scenes/overlay-message';

export interface IReportErrorOverlayProps extends WithTranslation {
    reportErrorOverlay: GenericOverlayOperation;
    captchaOperation: IOperationsState['CAPTCHA'];
    reportErrorSubmitOperation: IOperation;
}
export interface IReportErrorOverlayState {
    name: string;
    email: string;
    message: string;
    attachments: File[];
    captcha_value: string;

    errors: {
        name: string;
        email: string;
        message: string;
        captcha_value: string;
    };

    showSubmitSuccess: boolean;
}

export interface ReportErrorFormPayload {
    name: string;
    email: string;
    message: string;
    attachments: File[];
    captcha_key: string;
    captcha_value: string;
    security_code: string;
}

const mapStateToProps = (state: IState) => ({
    reportErrorOverlay: getOperationSafe(state, 'REPORT_ERROR_OVERLAY'),
    captchaOperation: getOperationSafe(state, 'CAPTCHA'),
    reportErrorSubmitOperation: getOperationSafe(state, 'SUBMIT_ERROR_OVERLAY')
});

/* eslint-disable @typescript-eslint/indent */
class ReportErrorOverlayComponent extends React.Component<
    IReportErrorOverlayProps,
    IReportErrorOverlayState
> {
    /* eslint-enable @typescript-eslint/indent */

    constructor(props: IReportErrorOverlayProps) {
        super(props);

        this.state = this.getInitialState();

        this.onSendButtonClick = this.onSendButtonClick.bind(this);
        this.onCloseClick = this.onCloseClick.bind(this);
    }

    getInitialState(): IReportErrorOverlayState {
        return {
            name: '',
            email: '',
            message: '',
            attachments: [],
            captcha_value: '',

            errors: {
                name: null,
                email: null,
                message: null,
                captcha_value: null
            },

            showSubmitSuccess: false
        };
    }

    componentDidUpdate(
        prev_props: IReportErrorOverlayProps,
        prev_state: IReportErrorOverlayState
    ) {
        let {
            reportErrorOverlay: oldReportErrorOverlay,
            reportErrorSubmitOperation: oldReportErrorSubmitOperation
        } = prev_props;
        let {
            reportErrorOverlay,
            reportErrorSubmitOperation,
            t
        } = this.props;

        if (
            // Тут не стоит сравнение `=== false` потому что `data` может быть не объектом
            // соответственно `data.visible` будет не определен
            !oldReportErrorOverlay.data?.visible
            && reportErrorOverlay.data?.visible === true
        ) {
            appActions.loadNewCaptcha();
        }

        if (
            reportErrorSubmitOperation.status === EOperationStatus.FAIL
            && reportErrorSubmitOperation.status !== oldReportErrorSubmitOperation.status
        ) {
            /* eslint-disable-next-line */
            this.setState({
                captcha_value: '',
                errors: {
                    /* eslint-disable-next-line */
                    ...this.state.errors,
                    captcha_value: t('Неправильная капча')
                }
            });
        }

        if (
            reportErrorSubmitOperation.status === EOperationStatus.SUCCESS
            && reportErrorSubmitOperation.status !== oldReportErrorSubmitOperation.status
        ) {
            /* eslint-disable-next-line */
            this.setState({
                showSubmitSuccess: true
            });
        }
    }

    onSendButtonClick() {
        let { captchaOperation } = this.props;

        let hasErrors = this.validateForm();

        if (!hasErrors) {
            overlayWindowsActions.submitErrorForm({
                name: this.state.name,
                email: this.state.email,
                message: this.state.message,
                security_code: __SECURITY_CODE__,
                captcha_key: captchaOperation.data.CAPTCHA_KEY,
                captcha_value: this.state.captcha_value,
                attachments: this.state.attachments
            });
        }
    }

    onCloseClick() {
        this.setState(this.getInitialState());
        overlayWindowsActions.hideReportErrorOverlay();
    }

    validateForm() {
        let { t } = this.props;
        let {
            captcha_value,
            email,
            message,
            name
        } = this.state;

        let hasErrors = false;
        let { errors } = this.getInitialState();

        if (captcha_value.length === 0) {
            errors.captcha_value = t('Поле обязательно для заполнения');
            hasErrors = true;
        }

        if (email.length === 0) {
            errors.email = t('Поле обязательно для заполнения');
            hasErrors = true;
        }

        if (!validateEmail(email)) {
            errors.email = t('Email указан неверно');
            hasErrors = true;
        }

        if (message.length === 0) {
            errors.message = t('Поле обязательно для заполнения');
            hasErrors = true;
        }

        if (name.length === 0) {
            errors.name = t('Поле обязательно для заполнения');
            hasErrors = true;
        }

        if (hasErrors) {
            this.setState({ errors });
        }

        return hasErrors;
    }

    renderSubmitButton() {
        let { reportErrorSubmitOperation, t } = this.props;

        return (
            <Button
                disabled={reportErrorSubmitOperation.status === EOperationStatus.PENDING}
                text={t('Отправить')}
                onClick={this.onSendButtonClick}
            />
        );
    }

    render() {
        let { reportErrorOverlay, t } = this.props;

        if (!reportErrorOverlay.data?.visible) {
            return null;
        }

        return (
            <React.Fragment>
                <OverlayContainer
                    className='report-error-overlay'
                    containerClassname='form-wrapper'
                    backgroundOpacity={8}
                >
                    <CloseButton onClick={this.onCloseClick} />

                    <div className="form">
                        <AutoScrollbars className='padding-fix'>
                            <div className="form-header">
                                {t('Сообщить об ошибке')}
                            </div>
                            <div className="form-body">
                                <OverlayFormInput
                                    required
                                    placeholder={t('ФИО')}
                                    wrapperClassname='submit-application-input'
                                    value={this.state.name}
                                    error={this.state.errors.name}
                                    onChange={(value) => {
                                        this.setState({
                                            name: value
                                        });
                                    }}
                                />

                                <OverlayFormInput
                                    required
                                    placeholder={t('Email')}
                                    wrapperClassname='submit-application-input'
                                    value={this.state.email}
                                    error={this.state.errors.email}
                                    onChange={(value) => {
                                        this.setState({
                                            email: value
                                        });
                                    }}
                                />

                                <OverlayFormTextArea
                                    required
                                    placeholder={t('Сообщение')}
                                    wrapperClassname='submit-application-input'
                                    value={this.state.message}
                                    error={this.state.errors.message}
                                    onChange={(value) => {
                                        this.setState({
                                            message: value
                                        });
                                    }}
                                />
                            </div>

                            <FileChooser
                                files={this.state.attachments}
                                onAdd={(file) => {
                                    this.setState(prev_state => ({
                                        attachments: [
                                            ...prev_state.attachments,
                                            file
                                        ]
                                    }));
                                }}
                                onRemove={(file) => {
                                    this.setState((prev_state) => {
                                        let attachments = [ ...prev_state.attachments ];
                                        attachments.splice(
                                            attachments.indexOf(file),
                                            1
                                        );
                                        return { attachments };
                                    });
                                }}
                            />

                            <div
                                className="license-agreement"
                                dangerouslySetInnerHTML={{
                                    __html: t('LICENSE_AGREEMENT')
                                }}
                            />
                            <div className="form-footer">
                                <div className="footer-actions-wrapper">
                                    <Captcha
                                        captchaOperation={this.props.captchaOperation}
                                        renderInput={() => (
                                            <OverlayFormInput
                                                hideErrorMessage
                                                value={this.state.captcha_value}
                                                error={this.state.errors.captcha_value}
                                                variant='underlined'
                                                placeholder={t('Введите слово')}
                                                onChange={(value) => {
                                                    this.setState({
                                                        captcha_value: value
                                                    });
                                                }}
                                            />
                                        )}
                                    />

                                    {this.renderSubmitButton()}
                                </div>
                            </div>
                        </AutoScrollbars>
                    </div>
                </OverlayContainer>

                {
                    this.state.showSubmitSuccess && (
                        <OverlayMessage
                            messageText={t('Сообщение отправлено')}
                            onCloseButtonClick={this.onCloseClick}
                        />
                    )
                }
            </React.Fragment>
        );
    }
}

export const ReportErrorOverlay = connect(mapStateToProps)(withTranslation()(ReportErrorOverlayComponent));
